import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { MaskedTitle } from "@/components/common/MaskedTitle";

// Interior page hero on the dark ink background.
// Optional `image` renders a clipped-frame photo alongside the copy.
export const PageHero = ({
  label, titleLines, accentIndex = -1, subtitle, children,
  image, imageAlt = "", imagePosition = "center", badge,
}) => {
  const reduce = useReducedMotion();

  const Copy = (
    <>
      <Reveal><SectionLabel dark>{label}</SectionLabel></Reveal>
      <MaskedTitle
        className="mt-7 font-display font-800 tracking-tight text-5xl md:text-7xl lg:text-[5rem] leading-[0.95]"
        lines={titleLines}
        accentIndex={accentIndex}
      />
      {subtitle && (
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">{subtitle}</p>
        </Reveal>
      )}
      {children && <div className="mt-10">{children}</div>}
    </>
  );

  if (image) {
    return (
      <section className="relative bg-ink text-white overflow-hidden">
        <div className="pointer-events-none absolute -top-32 left-0 h-80 w-80 rounded-full bg-teal/15 blur-[120px]" />
        <div className="container-px pt-36 pb-20 md:pt-44 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">{Copy}</div>
          <div className="lg:col-span-5">
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40, clipPath: "inset(12% 0 12% 0)" }}
              animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0% 0)" }}
              transition={{ delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-xl border border-white/10 aspect-[4/5]"
            >
              <img src={image} alt={imageAlt} style={{ objectPosition: imagePosition }}
                className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
              {badge && (
                <div className="absolute left-4 bottom-4 glass-nav-dark rounded-lg border border-white/15 px-4 py-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal">{badge.label}</p>
                  <p className="mt-1 text-sm font-display font-600">{badge.value}</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-ink text-white overflow-hidden">
      <div className="pointer-events-none absolute -top-32 right-0 h-80 w-80 rounded-full bg-teal/15 blur-[120px]" />
      <div className="container-px pt-36 pb-20 md:pt-44 md:pb-24">{Copy}</div>
    </section>
  );
};
